import React, { useState, useRef, useEffect, useCallback } from 'react';
import BasePage from './BasePage';
import { Input, Button, Typography, Tag, Alert, Tooltip, Select, message as antMessage } from 'antd';
import {
    SendOutlined,
    ClearOutlined,
    ThunderboltOutlined,
    CopyOutlined,
    FilePdfOutlined,
    CheckOutlined,
    DownOutlined,
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import { jsPDF } from 'jspdf';
import { aiService, usuariosService } from '../services/api';
import { useAuth } from '../hooks/useAuth';

const { Text } = Typography;
const { TextArea } = Input;

// Chave por usuário — cada conta tem seu próprio histórico isolado
const getStorageKey = (userEmail) => `piadse_chat_history_${userEmail || 'guest'}`;
const MODEL_NAME = 'Amazon Nova Lite';

// ── Animação "Digitando..." ────────────────────────────────────────────────────
const TypingDots = () => (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center', padding: '4px 0' }}>
        {[0, 1, 2].map(i => (
            <div key={i} style={{
                width: 8, height: 8, borderRadius: '50%',
                background: '#52c41a',
                animation: `chatBounce 1.2s infinite ease-in-out`,
                animationDelay: `${i * 0.2}s`,
            }} />
        ))}
        <Text style={{ color: '#8c8c8c', fontSize: 12, marginLeft: 6 }}>Consultando dados...</Text>
        <style>{`
            @keyframes chatBounce {
                0%, 80%, 100% { transform: translateY(0); opacity: 0.6; }
                40% { transform: translateY(-6px); opacity: 1; }
            }
        `}</style>
    </div>
);

// ── Remove markdown e emojis para texto limpo (PDF / cópia) ──────────────────
function stripMarkdown(text) {
    return text
        .replace(/\*\*(.*?)\*\*/g, '$1')
        .replace(/\*(.*?)\*/g, '$1')
        .replace(/`(.*?)`/g, '$1')
        .replace(/#{1,6}\s/g, '')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

// Remove emojis e caracteres fora do Latin-1 (jsPDF só suporta Latin-1)
function sanitizePdf(text) {
    return text
        // Remove emojis e símbolos Unicode fora do range Latin-1
        .replace(/[^\u0000-\u00FF]/g, '')
        // Remove espaços duplicados resultantes
        .replace(/  +/g, ' ')
        .trim();
}

// ── Bolha de mensagem ─────────────────────────────────────────────────────────
const MessageBubble = ({ msg, onSuggestionClick }) => {
    const isUser = msg.role === 'user';
    const [copied, setCopied] = useState(false);
    const hora = msg.timestamp
        ? new Date(msg.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
        : '';

    const handleCopy = () => {
        const text = stripMarkdown(msg.content);
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <div style={{
            display: 'flex',
            flexDirection: isUser ? 'row-reverse' : 'row',
            alignItems: 'flex-end',
            gap: 10,
            marginBottom: 20,
        }}>
            {/* Avatar */}
            <div style={{
                width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
                background: isUser
                    ? 'linear-gradient(135deg, #fa8c16, #d46b08)'
                    : 'linear-gradient(135deg, #389e0d, #52c41a)',
                boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
            }}>
                {isUser ? '👤' : '🤖'}
            </div>

            {/* Conteúdo */}
            <div style={{ maxWidth: '75%', display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start' }}>
                <div style={{
                    padding: '12px 16px',
                    borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                    background: isUser
                        ? 'linear-gradient(135deg, #fa8c16, #d46b08)'
                        : '#fff',
                    color: isUser ? '#fff' : '#1a1a1a',
                    border: isUser ? 'none' : '1px solid #d9f7be',
                    boxShadow: isUser ? '0 2px 8px rgba(250,140,22,0.25)' : '0 2px 8px rgba(0,0,0,0.06)',
                    fontSize: 14,
                    lineHeight: 1.65,
                    position: 'relative',
                }}>
                    {isUser ? (
                        <span>{msg.content}</span>
                    ) : (
                        <ReactMarkdown
                            components={{
                                p: ({ children }) => <p style={{ margin: '0 0 8px 0' }}>{children}</p>,
                                strong: ({ children }) => <strong style={{ color: '#389e0d' }}>{children}</strong>,
                                li: ({ children }) => <li style={{ marginBottom: 4 }}>{children}</li>,
                                code: ({ children }) => (
                                    <code style={{ background: '#f6ffed', padding: '1px 6px', borderRadius: 4, fontSize: 12, color: '#389e0d' }}>
                                        {children}
                                    </code>
                                ),
                            }}
                        >
                            {msg.content}
                        </ReactMarkdown>
                    )}

                    {/* Tags de fontes */}
                    {msg.sources?.length > 0 && (
                        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {msg.sources.slice(0, 3).map((s, i) => (
                                <Tag key={i} color="green" style={{ fontSize: 10, margin: 0 }}>{s}</Tag>
                            ))}
                        </div>
                    )}
                </div>

                {/* Hora + botão copiar */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, paddingLeft: 4, paddingRight: 4 }}>
                    {hora && (
                        <Text style={{ fontSize: 10, color: '#bfbfbf' }}>{hora}</Text>
                    )}
                    <Tooltip title={copied ? 'Copiado!' : 'Copiar mensagem'}>
                        <Button
                            type="text"
                            size="small"
                            icon={copied ? <CheckOutlined style={{ color: '#52c41a' }} /> : <CopyOutlined />}
                            onClick={handleCopy}
                            style={{ color: '#bfbfbf', padding: '0 4px', height: 18, fontSize: 11 }}
                        />
                    </Tooltip>
                </div>

                {/* Sugestões de próximas perguntas */}
                {!isUser && msg.suggestions?.length > 0 && (
                    <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {msg.suggestions.map((s, i) => (
                            <button
                                key={i}
                                onClick={() => onSuggestionClick(s)}
                                style={{
                                    background: '#f6ffed',
                                    border: '1px solid #b7eb8f',
                                    borderRadius: 16,
                                    padding: '4px 12px',
                                    fontSize: 12,
                                    color: '#389e0d',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                }}
                                onMouseEnter={e => { e.target.style.background = '#d9f7be'; }}
                                onMouseLeave={e => { e.target.style.background = '#f6ffed'; }}
                            >
                                💬 {s}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

// ── Gerar PDF da conversa ─────────────────────────────────────────────────────
function gerarPdfConversa(messages, userName) {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 15;
    const maxWidth = pageW - margin * 2;
    let y = 20;

    const addPage = () => {
        doc.addPage();
        y = 20;
    };

    const checkY = (needed = 10) => {
        if (y + needed > 280) addPage();
    };

    // ── Cabeçalho ──
    doc.setFillColor(35, 120, 4);
    doc.rect(0, 0, pageW, 16, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('Assistente PIADSE - Heliosfera Energias', margin, 11);

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    const dataExport = new Date().toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
    doc.text(`Exportado em: ${dataExport}`, pageW - margin - 50, 11);

    y = 26;

    // ── Subtítulo ──
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(100, 100, 100);
    doc.text(
        `Modelo: ${MODEL_NAME}  |  Usuario: ${sanitizePdf(userName || 'Nao identificado')}`,
        margin, y,
    );
    y += 8;

    // ── Linha separadora ──
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, y, pageW - margin, y);
    y += 6;

    // ── Mensagens ──
    messages.forEach((msg, idx) => {
        if (msg.role === 'assistant' && idx === 0) return; // pula saudação inicial

        const isUser = msg.role === 'user';
        const hora = msg.timestamp
            ? new Date(msg.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
            : '';

        // Labels sem emojis — jsPDF não suporta Unicode acima de Latin-1
        const remetente = isUser
            ? sanitizePdf(userName || 'Usuario')
            : 'Assistente PIADSE';

        const textoBruto = sanitizePdf(stripMarkdown(msg.content));

        checkY(14);

        // Fundo colorido sutil no label
        const labelH = 6;
        doc.setFillColor(isUser ? 255 : 240, isUser ? 245 : 250, isUser ? 230 : 240);
        doc.roundedRect(margin - 2, y - 4, maxWidth + 4, labelH, 1, 1, 'F');

        // Label do remetente
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(isUser ? 180 : 35, isUser ? 100 : 120, isUser ? 0 : 4);
        doc.text(
            `${isUser ? '[Voce]' : '[PIADSE]'}  ${remetente}${hora ? `  (${hora})` : ''}`,
            margin, y,
        );
        y += 6;

        // Corpo da mensagem
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(40, 40, 40);
        const linhas = doc.splitTextToSize(textoBruto, maxWidth - 4);
        linhas.forEach(linha => {
            checkY(6);
            doc.text(linha, margin + 4, y);
            y += 5.5;
        });

        y += 5;

        // Linha divisória leve entre mensagens
        if (idx < messages.length - 1) {
            checkY(4);
            doc.setDrawColor(235, 235, 235);
            doc.line(margin, y - 1, pageW - margin, y - 1);
            y += 3;
        }
    });

    // ── Rodapé em todas as páginas ──
    const totalPages = doc.internal.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p);
        doc.setFontSize(8);
        doc.setTextColor(160, 160, 160);
        doc.text(
            `Heliosfera Energias - PIADSE  |  Pagina ${p} de ${totalPages}`,
            pageW / 2,
            290,
            { align: 'center' },
        );
    }

    const nomeArquivo = `PIADSE_Chat_${new Date().toISOString().slice(0, 10)}.pdf`;
    doc.save(nomeArquivo);
}

// ── Componente principal ──────────────────────────────────────────────────────
const ChatPage = () => {
    const { user } = useAuth();

    const mensagemInicial = {
        role: 'assistant',
        content: `Olá! Sou o **Assistente IA** da heliosfera energias, especialista em energia solar e microgrid ⚡\n\nMonitoro sites com suas usinas, equipamentos, geração solar, baterias, carregadores EV e dados de mercado (CCEE/PLD). Cada **site** pode ter uma ou mais **usinas** (plantas GoodWe).\n\nO que quer saber?`,
        timestamp: new Date().toISOString(),
        suggestions: [
            'Qual a geração atual das usinas?',
            'Como estão as baterias agora?',
            'O PLD está favorável hoje?',
            'Existe alguma anomalia nos equipamentos?',
        ],
    };

    const storageKey = getStorageKey(user?.email);

    // Carregar histórico do localStorage — apenas do usuário atual
    const carregarHistorico = useCallback(() => {
        try {
            const salvo = localStorage.getItem(storageKey);
            if (salvo) {
                const parsed = JSON.parse(salvo);
                if (Array.isArray(parsed) && parsed.length > 0) return parsed;
            }
        } catch {
            // ignora erros de parse
        }
        return [mensagemInicial];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storageKey]);

    const [messages, setMessages] = useState(carregarHistorico);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const bottomRef = useRef(null);
    const inputRef = useRef(null);

    // Dropdown de empresa — apenas para super admin (sem empresaClienteId)
    const isSuperAdmin = !user?.empresaClienteId && (
        ['ADMIN', 'ROLE_ADMIN', 'ROLE_DEV'].some(r => user?.role?.includes(r))
    );
    const [empresas, setEmpresas] = useState([]);
    const [chatEmpresaId, setChatEmpresaId] = useState(null);
    const [chatEmpresaNome, setChatEmpresaNome] = useState('Heliosfera');

    useEffect(() => {
        if (!isSuperAdmin) return;
        usuariosService.getEmpresas().then(data => {
            const lista = Array.isArray(data) ? data : (data?.data || []);
            setEmpresas(lista);
        }).catch(() => {});
    }, [isSuperAdmin]);

    // Quando o usuário logado mudar, limpar o chat e carregar o histórico correto
    useEffect(() => {
        // Limpa chaves de outros usuários para não acumular dados de sessões antigas
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith('piadse_chat_history_') && key !== storageKey) {
                localStorage.removeItem(key);
            }
        });
        setMessages(carregarHistorico());
        setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storageKey]);

    // Salvar no localStorage sempre que as mensagens mudarem
    useEffect(() => {
        try {
            localStorage.setItem(storageKey, JSON.stringify(messages));
        } catch {
            // ignora se storage cheio
        }
    }, [messages, storageKey]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, loading]);

    const sendMessage = async (text) => {
        const msg = (text || input).trim();
        if (!msg || loading) return;

        setInput('');
        setError(null);

        const userMsg = { role: 'user', content: msg, timestamp: new Date().toISOString() };
        const newMessages = [...messages, userMsg];
        setMessages(newMessages);
        setLoading(true);

        try {
            const history = messages.map(m => ({ role: m.role, content: m.content }));
            const empresaCtx = isSuperAdmin ? chatEmpresaId : user?.empresaClienteId;
            const result = await aiService.chat(msg, history, empresaCtx);

            setMessages(prev => [...prev, {
                role: 'assistant',
                content: result.reply || 'Sem resposta.',
                sources: result.sources,
                suggestions: result.suggestions || [],
                timestamp: new Date().toISOString(),
            }]);
        } catch (err) {
            console.error('Erro no chat:', err);
            setError('Erro ao conectar com o assistente. Verifique se o backend está rodando.');
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: '⚠️ Não consegui processar sua pergunta agora. Tente novamente em instantes.',
                timestamp: new Date().toISOString(),
            }]);
        } finally {
            setLoading(false);
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const clearChat = () => {
        const inicial = { ...mensagemInicial, timestamp: new Date().toISOString() };
        setMessages([inicial]);
        setError(null);
        localStorage.removeItem(storageKey);
    };

    const handleExportPdf = () => {
        if (messages.length <= 1) {
            antMessage.warning('Nenhuma conversa para exportar.');
            return;
        }
        gerarPdfConversa(messages, user?.name || user?.email || 'Usuário');
        antMessage.success('PDF gerado com sucesso!');
    };

    const onlineStatus = !loading;

    return (
        <BasePage>
            <div style={{ maxWidth: 860, margin: '0 auto', padding: '0 12px' }}>

                {/* ── HEADER ── */}
                <div style={{
                    borderRadius: '14px 14px 0 0',
                    background: 'linear-gradient(135deg, #237804 0%, #389e0d 40%, #52c41a 100%)',
                    padding: '18px 22px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    boxShadow: '0 4px 16px rgba(82,196,26,0.2)',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <div style={{ position: 'relative' }}>
                            <div style={{
                                width: 50, height: 50, borderRadius: '50%',
                                background: 'rgba(255,255,255,0.2)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 26,
                                border: '2px solid rgba(255,255,255,0.3)',
                            }}>
                                🤖
                            </div>
                            <div style={{
                                position: 'absolute', bottom: -2, right: -2,
                                width: 16, height: 16, borderRadius: '50%',
                                background: '#fadb14',
                                border: '2px solid #fff',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 9,
                            }}>☀️</div>
                        </div>
                        <div>
                            <Text strong style={{ color: '#fff', fontSize: 18, display: 'block', lineHeight: 1.2 }}>
                                Assistente IA da heliosfera energias
                            </Text>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                                <div style={{
                                    width: 7, height: 7, borderRadius: '50%',
                                    background: onlineStatus ? '#fadb14' : '#8c8c8c',
                                    animation: onlineStatus ? 'chatPulse 2s infinite' : 'none',
                                }} />
                                <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12 }}>
                                    {loading ? 'Consultando...' : 'Especialista em Energia Solar & Microgrid'}
                                </Text>
                            </div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {/* Badge de empresa — dropdown para super admin, texto fixo para os demais */}
                        <div style={{
                            background: 'rgba(255,255,255,0.15)',
                            borderRadius: 20, padding: isSuperAdmin ? '2px 6px 2px 10px' : '4px 10px',
                            display: 'flex', alignItems: 'center', gap: 5,
                        }}>
                            <ThunderboltOutlined style={{ color: '#fadb14', fontSize: 12 }} />
                            {isSuperAdmin ? (
                                <Select
                                    size="small"
                                    value={chatEmpresaId}
                                    onChange={(val, opt) => {
                                        setChatEmpresaId(val);
                                        setChatEmpresaNome(opt?.label || 'Heliosfera');
                                    }}
                                    placeholder="Heliosfera (Global)"
                                    allowClear
                                    style={{ minWidth: 140 }}
                                    suffixIcon={<DownOutlined style={{ color: '#fff', fontSize: 10 }} />}
                                    styles={{
                                        popup: { root: { minWidth: 180 } },
                                    }}
                                    variant="borderless"
                                    className="chat-empresa-select"
                                    options={[
                                        { value: null, label: 'Heliosfera (Global)' },
                                        ...empresas.map(e => ({ value: e.id, label: e.nomeEmpresa || e.nome })),
                                    ]}
                                />
                            ) : (
                                <Text style={{ color: '#fff', fontSize: 11 }}>
                                    {user?.empresaClienteNome || 'Heliosfera'}
                                </Text>
                            )}
                        </div>

                        {/* Botão PDF */}
                        <Tooltip title="Exportar conversa em PDF">
                            <Button
                                icon={<FilePdfOutlined />}
                                onClick={handleExportPdf}
                                size="small"
                                style={{
                                    background: 'rgba(255,255,255,0.15)',
                                    border: '1px solid rgba(255,255,255,0.3)',
                                    color: '#fff', borderRadius: 8, fontSize: 11,
                                }}
                            >
                                PDF
                            </Button>
                        </Tooltip>

                        {/* Botão Limpar */}
                        <Button
                            icon={<ClearOutlined />}
                            onClick={clearChat}
                            size="small"
                            style={{
                                background: 'rgba(255,255,255,0.15)',
                                border: '1px solid rgba(255,255,255,0.3)',
                                color: '#fff', borderRadius: 8, fontSize: 11,
                            }}
                        >
                            Limpar
                        </Button>
                    </div>
                </div>

                {/* ── ÁREA DE MENSAGENS ── */}
                <div style={{
                    background: 'linear-gradient(180deg, #f9fffe 0%, #fff 100%)',
                    minHeight: '54vh', maxHeight: '60vh',
                    overflowY: 'auto',
                    padding: '20px 20px 10px',
                    border: '1px solid #d9f7be',
                    borderTop: 'none',
                    borderBottom: 'none',
                }}>
                    {error && (
                        <Alert
                            message={error}
                            type="error"
                            closable
                            onClose={() => setError(null)}
                            style={{ marginBottom: 16, borderRadius: 8 }}
                        />
                    )}

                    {messages.map((msg, idx) => (
                        <MessageBubble key={idx} msg={msg} onSuggestionClick={sendMessage} />
                    ))}

                    {loading && (
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, marginBottom: 20 }}>
                            <div style={{
                                width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                                background: 'linear-gradient(135deg, #389e0d, #52c41a)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
                            }}>🤖</div>
                            <div style={{
                                padding: '12px 18px',
                                background: '#fff', border: '1px solid #d9f7be',
                                borderRadius: '18px 18px 18px 4px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                            }}>
                                <TypingDots />
                            </div>
                        </div>
                    )}
                    <div ref={bottomRef} />
                </div>

                {/* ── INPUT ── */}
                <div style={{
                    background: '#fff',
                    border: '1px solid #d9f7be',
                    borderTop: '1px solid #e8e8e8',
                    borderRadius: '0 0 14px 14px',
                    padding: '12px 16px 8px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
                }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                        <TextArea
                            ref={inputRef}
                            value={input}
                            onChange={e => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Pergunte sobre seus sites, equipamentos, geração de energia, PLD... (Enter para enviar)"
                            autoSize={{ minRows: 1, maxRows: 5 }}
                            disabled={loading}
                            style={{
                                flex: 1, borderRadius: 12,
                                border: '1px solid #d9f7be',
                                resize: 'none', fontSize: 14,
                                background: '#f9fffe',
                            }}
                        />
                        <Button
                            type="primary"
                            icon={<SendOutlined />}
                            onClick={() => sendMessage()}
                            disabled={loading || !input.trim()}
                            style={{
                                height: 40, width: 40,
                                borderRadius: 12, flexShrink: 0,
                                background: input.trim() ? 'linear-gradient(135deg, #389e0d, #52c41a)' : '#d9d9d9',
                                border: 'none',
                                boxShadow: input.trim() ? '0 2px 8px rgba(82,196,26,0.35)' : 'none',
                                transition: 'all 0.2s',
                            }}
                        />
                    </div>

                    {/* Rodapé com info do modelo */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            ⚡ Enter para enviar · Shift+Enter para nova linha
                        </Text>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#52c41a' }} />
                            <Text style={{ fontSize: 10, color: '#8c8c8c' }}>
                                {MODEL_NAME}
                            </Text>
                        </div>
                    </div>
                </div>

                <style>{`
                    @keyframes chatPulse {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.4; }
                    }
                    .chat-empresa-select .ant-select-selector {
                        background: transparent !important;
                        border: none !important;
                        box-shadow: none !important;
                        color: #fff !important;
                        font-size: 11px !important;
                        padding: 0 4px !important;
                    }
                    .chat-empresa-select .ant-select-selection-placeholder,
                    .chat-empresa-select .ant-select-selection-item {
                        color: #fff !important;
                        font-size: 11px !important;
                    }
                    .chat-empresa-select .ant-select-arrow {
                        color: rgba(255,255,255,0.7) !important;
                    }
                    .chat-empresa-select .ant-select-clear {
                        background: transparent !important;
                        color: rgba(255,255,255,0.7) !important;
                    }
                `}</style>
            </div>
        </BasePage>
    );
};

export default ChatPage;
